import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea, Label, Select } from '@/components/ui'
import { Braces, Copy, Check, Trash2, Minimize2, Maximize2 } from 'lucide-react'
import { toast } from 'sonner'

export function JsonFormatter() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState('')
  const [indentSize, setIndentSize] = useState(2)
  const [copied, setCopied] = useState(false)

  const formatJson = () => {
    setError('')
    if (!input.trim()) {
      setOutput('')
      return
    }

    try {
      const parsed = JSON.parse(input)
      const formatted = JSON.stringify(parsed, null, indentSize)
      setOutput(formatted)
      toast.success('JSON formatted successfully')
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Invalid JSON'
      setError(errorMessage)
      setOutput('')
      toast.error('Invalid JSON')
    }
  }

  const minifyJson = () => {
    setError('')
    if (!input.trim()) {
      setOutput('')
      return
    }

    try {
      const parsed = JSON.parse(input)
      const minified = JSON.stringify(parsed)
      setOutput(minified)
      toast.success('JSON minified successfully')
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Invalid JSON'
      setError(errorMessage)
      setOutput('')
      toast.error('Invalid JSON')
    }
  }

  const copyOutput = async () => {
    if (!output) return

    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
    setError('')
  }

  const loadSample = () => {
    const sample = {
      name: "SCB Terminal",
      version: "2.0.0",
      features: ["tools", "utilities", "generators"],
      config: {
        theme: "dark",
        language: "en",
        notifications: true
      },
      stats: {
        tools: 70,
        users: 1000,
        uptime: 99.9
      }
    }
    setInput(JSON.stringify(sample))
  }

  const getStats = () => {
    if (!output) return null
    try {
      const parsed = JSON.parse(output)
      const keys = JSON.stringify(parsed).match(/"[^"]+"\s*:/g)?.length || 0
      const arrays = JSON.stringify(parsed).match(/\[/g)?.length || 0
      const objects = JSON.stringify(parsed).match(/\{/g)?.length || 0
      return { keys, arrays, objects, size: output.length }
    } catch {
      return null
    }
  }

  const stats = getStats()

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Braces className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">JSON Formatter</h1>
            <p className="text-muted-foreground">Format, validate, and minify JSON data</p>
          </div>
        </div>
      </div>

      {/* Options bar */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="indent" className="whitespace-nowrap">Indent:</Label>
              <Select
                id="indent"
                value={String(indentSize)}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="w-20"
              >
                <option value="2">2 spaces</option>
                <option value="4">4 spaces</option>
                <option value="1">1 tab</option>
              </Select>
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clearAll}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Paste your JSON here</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder='{"key": "value"}'
              className="min-h-[300px] font-mono text-sm"
            />
            <div className="flex gap-2">
              <Button onClick={formatJson} className="flex-1">
                <Maximize2 className="h-4 w-4 mr-2" />
                Format
              </Button>
              <Button onClick={minifyJson} variant="outline" className="flex-1">
                <Minimize2 className="h-4 w-4 mr-2" />
                Minify
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>Formatted result</CardDescription>
              </div>
              {output && (
                <Button variant="outline" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={error || output}
              readOnly
              placeholder="Formatted JSON will appear here..."
              className={`min-h-[300px] font-mono text-sm ${error ? 'text-destructive' : ''}`}
            />
            {stats && (
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span>{stats.keys} keys</span>
                <span>{stats.objects} objects</span>
                <span>{stats.arrays} arrays</span>
                <span>{stats.size} chars</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
